import type { AxiosError } from 'axios'
import {
  api,
  handleApiError,
  handleApiResponse,
  type ServiceResponse,
} from '../ApiService'

export interface Role {
  roleId: number
  roleCode: string
  displayName: string
  remarks?: string
  isActive?: boolean
}

export interface User {
  userId: number
  username: string
  firstName: string
  lastName: string
  email: string
  role: Role
  keycloakUserId: string
  orgId: string
  opunitId: string
  isActive: boolean
}

export interface CreateUserPayload {
  username: string
  firstName: string
  lastName: string
  email: string
  roleId?: number
  orgId: string
  opunitId: string
  isActive: boolean
}

export interface UpdateUserPayload {
  firstName?: string
  lastName?: string
  email?: string
}

export interface UpdateUserRolePayload {
  roleId: number
}

export interface PaginatedResponse<T> {
  content: T[]
  pageable: {
    pageNumber: number
    pageSize: number
    sort: any[]
    offset: number
    paged: boolean
    unpaged: boolean
  }
  totalElements: number
  totalPages: number
  last: boolean
  size: number
  number: number
  sort: any[]
  numberOfElements: number
  first: boolean
  empty: boolean
}

/**
 * Get all users with optional role filter
 */
export const getUsers = async (
  isActive?: string,
  page: number = 0,
  size: number = 10,
  query?: string
): Promise<ServiceResponse<PaginatedResponse<User>>> => {
  try {
    const params: any = {
      page,
      size,
    }

    // Add isActive parameter only if provided
    if (isActive !== undefined) {
      params.isActive = isActive
    }

    // Add q parameter (not query) for search - matching the API expectation
    if (query?.trim()) {
      params.q = query.trim()
    }

    const response = await api.get<PaginatedResponse<User>>(
      '/master-data/users',
      { params }
    )
    return handleApiResponse<PaginatedResponse<User>>(response)
  } catch (error) {
    return handleApiError<PaginatedResponse<User>>(error as AxiosError)
  }
}

/**
 * Get user by ID
 */
export const getUserById = async (
  userId: number
): Promise<ServiceResponse<User>> => {
  try {
    const response = await api.get<User>(`/master-data/users/${userId}`)
    return handleApiResponse<User>(response)
  } catch (error) {
    return handleApiError<User>(error as AxiosError)
  }
}

/**
 * Create a new user
 */
export const createUser = async (
  userData: CreateUserPayload
): Promise<ServiceResponse<User>> => {
  try {
    const response = await api.post<User>('/master-data/users', userData)
    return handleApiResponse<User>(response)
  } catch (error) {
    return handleApiError<User>(error as AxiosError)
  }
}

/**
 * Update user details (firstName, lastName)
 */
export const updateUser = async (
  userId: number,
  userData: UpdateUserPayload
): Promise<ServiceResponse<User>> => {
  try {
    const response = await api.put<User>(
      `/master-data/users/${userId}`,
      userData
    )
    return handleApiResponse<User>(response)
  } catch (error) {
    return handleApiError<User>(error as AxiosError)
  }
}

/**
 * Update user role
 */
export const updateUserRole = async (
  userId: number,
  roleData: UpdateUserRolePayload
): Promise<ServiceResponse<User>> => {
  try {
    const response = await api.put<User>(
      `/master-data/users/${userId}/role`,
      roleData
    )
    return handleApiResponse<User>(response)
  } catch (error) {
    return handleApiError<User>(error as AxiosError)
  }
}

/**
 * Deactivate user
 */
export const deactivateUser = async (
  userId: number
): Promise<ServiceResponse<null>> => {
  try {
    const response = await api.patch<null>(
      `/master-data/users/${userId}/deactivate`
    )
    return handleApiResponse<null>(response)
  } catch (error) {
    return handleApiError<null>(error as AxiosError)
  }
}

/**
 * Activate user (reactivate)
 */
export const activateUser = async (
  userId: number
): Promise<ServiceResponse<null>> => {
  try {
    const response = await api.patch<null>(
      `/master-data/users/${userId}/activate`
    )
    return handleApiResponse<null>(response)
  } catch (error) {
    return handleApiError<null>(error as AxiosError)
  }
}

/**
 * Get user access roles details
 */
export const getUserAccessRoles = async (
  token: string
): Promise<ServiceResponse> => {
  try {
    const response = await api.get('/master-data/access/bootstrap', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return handleApiResponse(response)
  } catch (error) {
    return handleApiError(error as AxiosError)
  }
}
